import { NgClass } from '@angular/common';
import { AfterViewInit, Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { LazyLoadEvent, PrimeTemplate, TableState } from 'primeng/api';
import { Button } from 'primeng/button';
import { Card } from 'primeng/card';
import { Dialog } from 'primeng/dialog';
import { Message } from 'primeng/message';
import { Select } from 'primeng/select';
import { SplitButton } from 'primeng/splitbutton';
import { Tag } from 'primeng/tag';
import { Tooltip } from 'primeng/tooltip';
import { SERVICE_ENDPOINTS } from '../../../../core/configs/service-endpoints';
import { CustomerDataDetailsDialogComponent } from '../../../../custom/components/admin/customer-data-details-dialog/customer-data-details-dialog.component';
import { CustomerDataEditDialogComponent } from '../../../../custom/components/admin/customer-data-edit-dialog/customer-data-edit-dialog.component';
import { SpsTableComponent } from '../../../../custom/components/sps-table/sps-table.component';
import { TableListProvider } from '../../../../custom/provider/table-list-provider';
import { CustomerStatus } from '../../../enums/organisation/customer-status';
import { OrganisationStatus } from '../../../enums/organisation/organisation-status';
import { OrganisationData } from '../../../interfaces/organisation/organisation-data';
import { OrganisationStatusTagPipe } from '../../../pipes/organisation-status-tag.pipe';
import { BaseAdminCustomerComponent } from '../../base/base-admin-customer.component';

@Component({
  selector: 'app-admin-onboarding',
  imports: [
    Card,
    PrimeTemplate,
    Button,
    SplitButton,
    Tooltip,
    SpsTableComponent,
    CustomerDataDetailsDialogComponent,
    Dialog,
    OrganisationStatusTagPipe,
    Tag,
    Select,
    FormsModule,
    Message,
    CustomerDataEditDialogComponent,
    NgClass
  ],
  templateUrl: './admin-onboarding.component.html'
})
export class AdminOnboardingComponent extends BaseAdminCustomerComponent implements OnInit, AfterViewInit {

  /**
   * Status change dialog
   * only extended params, see base class
   * @protected
   */
  protected readonly OrganisationStatus = OrganisationStatus;
  newStatus: OrganisationStatus = null;

  constructor() {
    super();
  }

  async ngOnInit() {
    /**
     * Workaround for table refresh error after filter reset
     * route to dummy component and route back
     * and delete the state for the other admin component
     */
    this.redirectRoute = 'adminonboarding';
    sessionStorage.removeItem('ADMIN_ORGANISATIONS_VIEW');

    this.loading = true;
    this.tableListProvider = new TableListProvider(this.i18n);
    this.tableConfig.set(await this.tableConfigService.getTableConfig('SPS_ADMIN_ONBOARDING_VIEW', false));
    this.rows = this.tableConfig().rows;
    if (this.userSessionSettingsProvider.hasValue('ADMIN_ONBOARDING_VIEW_ROWS')) {
      this.rows = this.userSessionSettingsProvider.get('ADMIN_ONBOARDING_VIEW_ROWS');
    }
    /**
     * data from resolver
     */
    this.referralPartnerList = this.route.snapshot.data['refPartnerList'];
    this.tableListProvider.addListByKey('REFERRALPARTNERLIST', this.referralPartnerList);
    this.dataCenterList = this.route.snapshot.data['dataCenterList'];
    /**
     * Init menu items for the split-button menu
     */
    this.initAdminMenuItems();
    /**
     * render the html template
     */
    this.loading = false;
    this.tableControlService.refreshTableConfig.set(false);
  }

  ngAfterViewInit(): void {
    /**
     * Some controls need refresh after language-changes
     */
    this.subscriptions.add(
      this.i18n.languageChanged.subscribe({
        next: value => {
          this.refreshControls = true;
          setTimeout(() => {
            this.refreshControls = false;
          }, 10);
        }
      })
    );

    /**
     * Initialize Breadcrumb-Items
     */
    setTimeout(() => {
      this.i18n.breadcrumbMenuItems.set([
        {
          icon: 'pi pi-cog',
          i18nLabelKey: 'MENUONBOARDING',
          label: this.i18n.i18nLng()['MENUONBOARDING'],
          routerLinkActive: true
        }
      ]);
    }, 50);

  }

  /**
   * Parent-Data-Provider
   * @param lazyLoadEvent
   */
  getOrganisationPageData = (lazyLoadEvent: LazyLoadEvent) => {
    this.lastLazyLoadingEvent = lazyLoadEvent;
    return this.customerDataService.findPage(lazyLoadEvent, SERVICE_ENDPOINTS.GET_ORGANISATIONS_ONBOARDING);
  };

  /**
   * if row was selected - open details dialog for specific organisation
   * @param organisationData
   */
  onSelectionChanged(organisationData: OrganisationData) {
    this.selectedRowData = null;
    this.selectedRowDataBackup = null;
    this.adminMenuActive = false;
    this.newStatusList = [];
    if (organisationData) {
      this.selectedRowData = organisationData;
      this.selectedRowDataBackup = this.helper.jsonCopy(organisationData);
      this.paymentDataService.selectedOrganisationData = this.selectedRowData;
      this.paymentDataService.selectedOrganisationId = this.selectedRowData.zbvOrganisationId;
      this.paymentDataService.selectedOrganisationName = this.selectedRowData.name;
      this.adminMenuActive = true;
      /**
       * init list for status change (available status)
       */
      this.newStatusList = this.tableListProvider.getListByKey('ORGASTATUS').filter(item => item.value !== this.selectedRowData.organisationStatus);
    }
  }

  /**
   * Rows changed by user
   * @param newRowCount
   */
  rowsCountChanged(newRowCount: number) {
    this.rows = newRowCount;
    this.userSessionSettingsProvider.set('ADMIN_ONBOARDING_VIEW_ROWS', this.rows);
  }

  /**
   * Inititalize the menu-items for the admin views
   * of the customer data
   * @private
   */
  private initAdminMenuItems(): void {
    this.adminMenuItems = [];
    this.adminMenuItems.push(
      {
        id: 'm-customer-data',
        label: this.i18n.i18nLng()['CUSTOMERDATA'],
        icon: 'pi pi-file-edit',
        command: () => {
          this.initOrganisationDataInvalidFlags();
          this.showCustomerDataDialog = true;
        }
      },
      {
        id: 'm-payment-types',
        label: this.i18n.i18nLng()['MENUPAYMENTTYPES'],
        icon: 'pi pi-wallet',
        command: () => {
          this.router.navigate(['/adminpaymenttypes/onboarding/' + this.selectedRowData?.zbvOrganisationId])
        },
      },
      /** Payment Page aktuell ausgeblendet - wird nach go live umgesetzt
       {
        id: 'm-payment-page',
        label: this.i18n.i18nLng()['MENUPAYMENTPAGE'],
        icon: 'pi pi-desktop'
      },
       */
      {
        separator: true
      },
      {
        id: 'm-change-status',
        label: this.i18n.i18nLng()['PROGRESSSTATUS'],
        icon: 'pi pi-arrow-right-arrow-left',
        command: () => {
          this.showStatusChangeDialog = true;
        }
      }
    );
  }

  /**
   * Table state was restored
   * @protected
   * @param tableState
   */
  protected onStateRestored(tableState: TableState) {
    this.onSelectionChanged(tableState?.selection);
  }

  /**
   * save the changed Organisation-Status of the onboarding process
   * check if onboarding is completed, and when - set the corresponding CustomerStatus
   * @protected
   */
  protected saveNewStatus() {
    let resetRowSelection: boolean = false;
    this.selectedRowData.organisationStatus = this.newStatus;
    if (this.newStatus === OrganisationStatus.CUSTOMER) {
      this.selectedRowData.customerStatus = CustomerStatus.CUSTOMER;
      // dataRecord is not longer available in onboarding view, so the row selection should be reset in the data-table after successful update
      resetRowSelection = true;
    }
    this.saveOrganisationData(resetRowSelection);
  }

}
